import { CUSTOM_PLAN } from "../../constants/landingPage";
import { LeftArrow } from "../icons/Icons";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { useCurrency } from "../../hooks/useCurrency";

export default function PlanSummary({
  planType,
  couponCode,
  setCouponCode,
  pricingData,
  isFlatPricing,
  setCurrentTab,
  quantity,
  isBasketStep,
  openDemoModal,
  onApplyCoupon,
  onClearCoupon,
  isCouponApplied,
  isInviteFlow = false,
  invoiceDetails = null,
  status = null,
}) {
  const { convertAndFormat } = useCurrency();
  const [isValidating, setIsValidating] = useState(false);

  // Use invoiceDetails if provided, otherwise use pricingData
  const displayData = invoiceDetails ? {
    totalPrice: invoiceDetails.total_price,
    discount: (Number(invoiceDetails?.discount_amount) || 0) + (Number(invoiceDetails?.coupon_amount) || 0),
    regularPrice: invoiceDetails.regular_price,
    basePrice: invoiceDetails.base_price,
    unitPrice: invoiceDetails.unit_price,
    isTrial: invoiceDetails.trial_days > 0,
    trialDays: invoiceDetails.trial_days,
    priceAfterTrial: invoiceDetails.price_after_trial,
    subscriptionDuration: invoiceDetails.subscription_duration,
    couponDetails: {
      discount_value: invoiceDetails.coupon_amount,
      discount_type: invoiceDetails.coupon_discount_type,
    },
    applyDurationType: invoiceDetails.coupon_duration_type || invoiceDetails.discount_duration_type,
    applyCycles: invoiceDetails.coupon_allowed_cycles || invoiceDetails.discount_allowed_cycles,
  } : pricingData;

  const {
    totalPrice,
    discount,
    regularPrice,
    basePrice,
    unitPrice,
    isTrial = false,
    trialDays = 0,
    priceAfterTrial,
    subscriptionDuration,
    couponDetails
  } = displayData || {};

  const displayQuantity = invoiceDetails ? invoiceDetails.seat_count : quantity;
  const discountWithSign = discount && discount > 0 ? -Math.abs(discount) : 0;

  const { t } = useTranslation('signup');
  const [showTrialNotice, setShowTrialNotice] = useState(false);
  const noticeRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (noticeRef.current && !noticeRef.current.contains(event.target)) {
        setShowTrialNotice(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isInvoiceMode = !!invoiceDetails;

  return (
    <>
      <div className={`description-summary ${isInvoiceMode ? 'invoice-mode' : ''}`}>
        {(planType !== CUSTOM_PLAN && !isInviteFlow && !isInvoiceMode) && (
        <div
          className="price-block"
          style={{ display: isBasketStep ? "block" : "none" }}
        >
          <h4>{t('do_you_have_coupon')}</h4>
          <div className="coupon-block">
            <span 
              className={`apply-btn ${isCouponApplied ? 'clear' : ''}`} 
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                if (isCouponApplied) {
                  onClearCoupon();
                } else {
                  if (!couponCode || isValidating) return;
                  setIsValidating(true);
                  await onApplyCoupon(couponCode);
                  setIsValidating(false);
                }
              }}
            >
              {isCouponApplied ? t('clear', 'Clear') : (isValidating ? t('validating', 'Validating...') : t('apply'))}
            </span>
            <input
              type="text"
              value={couponCode}
              disabled={isCouponApplied}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder={t('enter_coupon_here')}
            />
          </div>
          {isCouponApplied && (
            <div className="coupon-applied">
              {(() => {
                const isPercentage = couponDetails?.discount_type === 'percentage';
                const hasMax = couponDetails?.max_coupon_value > 0;
                const rawDiscountVal = isPercentage ? couponDetails?.discount_value : Number(couponDetails?.discount_value);
                const discountVal = isPercentage ? rawDiscountVal : convertAndFormat(Math.min(Number(rawDiscountVal), Number(regularPrice)));
                const maxVal = convertAndFormat(couponDetails?.max_coupon_value) || 0;

                if (isPercentage) {
                  return hasMax 
                    ? t('coupon_applied_percentage_with_max', { discount: discountVal, max: maxVal })
                    : t('coupon_applied_percentage', { discount: discountVal });
                } else {
                  return hasMax
                    ? t('coupon_applied_flat_with_max', { discount: discountVal, max: maxVal })
                    : t('coupon_applied_flat', { discount: discountVal });
                }
              })()}
            </div>
          )}
        </div>
        )}
        <div className="price-summary">
          <h6>{isInvoiceMode ? t('invoice_details', 'تفاصيل الفاتورة') : t('basket_summary')}</h6>
          <ul>
            {!isInvoiceMode && (
              <li>
                <span>{t('subscription_period')} </span>
                <label>
                  <span dir="ltr">{subscriptionDuration ? t(`subscriptionPeriod.${subscriptionDuration?.toLowerCase()}`) : '-'}</span>
                </label>
              </li>
            )}

            {isInvoiceMode && (
              <>
                <li>
                  <span>{t('user_count', "عدد المستخدمين")}</span>
                  <label><span dir="ltr">{displayQuantity}</span></label>
                </li>
                <li>
                  <span>{t('plan', "خطة")}</span>
                  <label><span>{invoiceDetails.plan_name}</span></label>
                </li>
              </>
            )}

            {(planType === CUSTOM_PLAN || isInviteFlow) && !isInvoiceMode ? (
              <>
                {
                  basePrice > 0 && (
                    <li>
                      <span>{t('base_price')} </span>
                      <label>
                        <span dir="ltr">{convertAndFormat(basePrice)}</span>
                      </label>
                    </li>
                  )
                }
                <li>
                  <span>{t('price_per_unit_seat')} </span>
                  <label>
                    <span dir="ltr">{convertAndFormat(unitPrice)}</span>
                  </label>
                </li>
                {!isFlatPricing && (
                  <li>
                    <span>{t('number_of_seats', 'Number of Seats')}</span>
                    <label>
                      <span dir="ltr">{displayQuantity}</span>
                    </label>
                  </li>
                )}
                {
                  (discount > 0 || isInviteFlow) && (
                    <>
                      <li>
                        <span>
                          {t('price_before_discount')}{" "}
                          <strong className={(isFlatPricing && !isInviteFlow) ? "d-none" : ""} dir="ltr" translate="no">
                            {convertAndFormat(unitPrice, true)} X {displayQuantity}
                          </strong>
                        </span>
                        <label>
                          <span dir="ltr">{convertAndFormat(regularPrice)}</span>
                        </label>
                      </li>
                      <li className="discount-value">
                        <span>{t('discount_value')} </span>
                        <label>
                          <span dir="ltr">{convertAndFormat(discountWithSign)}</span>
                        </label>
                      </li>
                    </>
                  )
                }
              </>
            ): (
              <>
                {!isFlatPricing && !isInvoiceMode && (
                  <li>
                    <span>{t('number_of_seats', 'Number of Seats')}</span>
                    <label>
                      <span dir="ltr">{displayQuantity}</span>
                    </label>
                  </li>
                )}
                {!isInvoiceMode && (
                  <li>
                    <span>
                      {t('price_before_discount')}{" "}
                      <strong className={isFlatPricing ? "d-none" : ""} dir="ltr" translate="no">
                        {convertAndFormat(unitPrice, true)} X {displayQuantity}
                      </strong>
                    </span>
                    <label>
                      <span dir="ltr">{convertAndFormat(regularPrice)}</span>
                    </label>
                  </li>
                )}
                {(discount > 0 && !isInvoiceMode) && (
                  <li className="discount-value">
                    <span>{t('discount_value')} </span>
                    <label>
                      <span dir="ltr">{convertAndFormat(discountWithSign)}</span>
                    </label>
                  </li>
                )}
              </>
            )}

            {isTrial && (
              <li className="discount">
                <span ref={noticeRef} onClick={() => setShowTrialNotice(!showTrialNotice)}>
                  {t("trial")}
                  <div className="popup-wrapper">
                    <i className="fas fa-info-circle ms-2" translate="no" style={{cursor: "pointer"}}></i>
                    <div className="popup-content">
                        {isTrial && showTrialNotice && (
                          <div className="trial-notice">
                            <p>
                              {t(
                                "trial_notice_combined",
                                `Start with a ${trialDays}-day free trial. After the trial ends, your subscription will automatically renew at ${priceAfterTrial}.`,
                                { trialDays, priceAfterTrial: convertAndFormat(priceAfterTrial) }
                              )}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </span>
                <label>
                  <span dir="ltr">{trialDays}</span> <span dir="rtl">{t('days')}</span>
                </label>
              </li>
            )}

            {isInvoiceMode && (
              <>
                <li>
                  <span>{t("transaction_number", "رقم العملية")}</span>
                  <label><span>{invoiceDetails.invoice_number}</span></label>
                </li>
                <li>
                  <span>{t("transaction_date", "تاريخ العملية")}</span>
                  <label><span>{invoiceDetails.transaction_date}</span></label>
                </li>
                <div className="divider" style={{height: '1px', background: 'rgba(255, 255, 255, 0.1)', margin: '16px 0', borderBottom: '1px dashed rgba(255, 255, 255, 0.2)'}}></div>
                <li>
                  <span>{t("payment_method_label", "طريقة الدفع")}</span>
                  <label><span>{invoiceDetails.payment_method}</span></label>
                </li>
                <li>
                  <span>{t("transaction_status", "حالة العملية")}</span>
                  <label>
                    {status === "success" && <span style={{ color: "#51CAAD" }}>{t("succeeded_status", "ناجحة")}</span>}
                    {status === "error" && <span style={{ color: "#BE1622" }}>{t("failed_status", "فشلت")}</span>}
                    {(status === "processing" || status === "polling" || status === "verification") && (
                      <span style={{ color: "#F79E1B" }}>{t("pending_status", "قيد المعالجة")}</span>
                    )}
                  </label>
                </li>
                <div className="divider" style={{height: '1px', background: 'rgba(255, 255, 255, 0.1)', margin: '16px 0', borderBottom: '1px dashed rgba(255, 255, 255, 0.2)'}}></div>
                <li>
                  <span>{t("reference_number", "رقم المرجع")}</span>
                  <label><span>{invoiceDetails.tap_track_id}</span></label>
                </li>
                <li>
                  <span>{t("purchase_transaction_number", "رقم عملية الشراء")}</span>
                  <label><span>{invoiceDetails.tap_receipt_id}</span></label>
                </li>
                <li>
                  <span>{t("email_label", "البريد الإلكتروني")}</span>
                  <label><span>{invoiceDetails.email}</span></label>
                </li>
                <div className="divider" style={{height: '1px', background: 'rgba(255, 255, 255, 0.1)', margin: '16px 0', borderBottom: '1px dashed rgba(255, 255, 255, 0.2)'}}></div>
              </>
            )}

            {(displayData?.applyDurationType) && (
              <li className="discount-type">
                <span>{t('discount_type', 'Discount Type')}</span>
                <label>
                  {displayData.applyDurationType === 'limited_cycles' 
                    ? t('limited_cycles_notice', `Limited ({{count}} cycles)`, { count: displayData.applyCycles })
                    : t(displayData.applyDurationType)}
                </label>
              </li>
            )}

            <li className="final-price">
              <span>{isInvoiceMode ? t("total_price_label", "السعر الكلي") : t('final_price')}</span>
              <label>
                <span dir="ltr">{convertAndFormat(totalPrice)}</span>
              </label>
            </li>
          </ul>
          
          {discount > 0 && (
            <div className="trial-notice" style={{ margin: '15px 0', padding: '12px', backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', textAlign: 'center', fontSize: '0.85rem', lineHeight: '1.5' }}>
              <p style={{ margin: 0, color: '#51CAAD' }}>
                {t(
                  "discount_notice",
                  `Volume discount applied. You saved ${discount} based on your selected seats.`,
                  { discount: convertAndFormat(discount) }
                )}
              </p>
            </div>
          )}

          {isBasketStep && !isInviteFlow && !isInvoiceMode && (
            <div className="price-summary-action mob-hide">
              <button
                onClick={() => setCurrentTab((prev) => prev + 1)}
                type="button"
                className="btn btn-primary btn-icons"
              >
                <span>{t('checkout')}</span>
                <p>
                  <span dir="ltr">{convertAndFormat(totalPrice)}</span>{" "}
                  <LeftArrow />
                </p>
              </button>
              
              {planType === CUSTOM_PLAN && (
                <>
                  <span className="">{t('or')}</span>
                  <button
                    onClick={openDemoModal}
                    type="button"
                    className="btn btn-primary"
                  >
                    <span>{t('contact_sales')}</span>
                  </button>
                </>
              )}
            </div>
          )}
          {!isInvoiceMode && (
            <div className="desc-note">
              {t('guarantee_note')}
            </div>
          )}
        </div>
      </div>
      {isBasketStep && !isInviteFlow && !isInvoiceMode && (
        <div className="price-summary-action mob-show">
          <div className="first-block">
            <button
              onClick={() => setCurrentTab((prev) => prev + 1)}
              type="button"
              className="btn btn-primary btn-icons"
            >
              <span>{t('continue')}</span>
              <LeftArrow />
            </button>
            <div className="price">
              <strong><span dir="ltr">{convertAndFormat(totalPrice)}</span></strong>
            </div>
          </div>
          {planType === CUSTOM_PLAN && (
            <div className="second-block">
              <button
                onClick={openDemoModal}
                type="button"
                className="btn btn-primary btn-icons"
              >
                <span>{t('request_demo')}</span>
              </button>
            </div>
          )}
        </div>
      )}
      {!isInvoiceMode && <div className="footer-action-space mob-show"></div>}
    </>
  );
}
